/* eslint-disable sonarjs/prefer-single-boolean-return */
/* eslint-disable sonarjs/no-duplicate-string */
'use client';

// Optimization Changes Flag
import React, { useCallback, useEffect, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';

import { useAuthContext } from '@/components/Providers/AuthProvider';

const TwoFactorModalFlow = dynamic(() => import('@/components/V2/Auth/TwoFactorModalFlow'), {
  loading: () => null,
  ssr: false,
});

const TWO_FA_MODAL_VALUES = ['enter2FA', 'enable2FA', 'trustedDevice'] as const;
const AUTH_PAGE_PATTERNS = ['/account/login', '/account/signup', '/login', '/signup'];

const shouldShow2FAModal = (searchParams: URLSearchParams | null): boolean => {
  if (!searchParams) return false;

  const modal = searchParams.get('modal');
  if (modal && TWO_FA_MODAL_VALUES.includes(modal as (typeof TWO_FA_MODAL_VALUES)[number])) {
    return true;
  }

  if (searchParams.get('show2FA') === 'true') return true;
  if (searchParams.get('enter2FA') === 'true') return true;

  return false;
};

const isAuthPage = (pathname: string | null): boolean => {
  if (!pathname) return false;
  return AUTH_PAGE_PATTERNS.some(pattern => pathname.includes(pattern));
};

export default function TwoFactorModalWrapper() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { customer, isLoggedIn } = useAuthContext();

  const [shouldMount, setShouldMount] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const hasCheckedInitialParams = useRef(false);

  const isCustomerDataReady = !!(customer?.email && customer.email.trim());

  const checkAndUpdateMountState = useCallback(() => {
    if (typeof window === 'undefined') return;

    const currentSearchParams = new URLSearchParams(window.location.search);
    const shouldShow = shouldShow2FAModal(currentSearchParams);

    if (shouldShow) {
      if (isLoggedIn && !isCustomerDataReady) {
        return;
      }
      setShouldMount(true);
    }
  }, [isLoggedIn, isCustomerDataReady]);

  const handlePopState = useCallback(() => {
    checkAndUpdateMountState();
  }, [checkAndUpdateMountState]);

  const handle2FATrigger = useCallback((event: Event) => {
    const customEvent = event as CustomEvent;
    if (customEvent.detail?.show) {
      setShouldMount(true);
    }
  }, []);

  const preloadModalChunk = useCallback(() => {
    if (isPreloaded) return;

    import('@/components/V2/Auth/TwoFactorModalFlow')
      .then(() => {
        setIsPreloaded(true);
      })
      .catch(() => {});
  }, [isPreloaded]);

  useEffect(() => {
    if (!hasCheckedInitialParams.current) {
      hasCheckedInitialParams.current = true;
      checkAndUpdateMountState();
    }

    const shouldShow = shouldShow2FAModal(searchParams);
    if (shouldShow) {
      if (isLoggedIn && !isCustomerDataReady) {
      } else {
        setShouldMount(true);
      }
    }
  }, [searchParams, checkAndUpdateMountState, isLoggedIn, isCustomerDataReady]);

  useEffect(() => {
    if (isCustomerDataReady && shouldShow2FAModal(searchParams)) {
      setShouldMount(true);
    }
  }, [isCustomerDataReady, searchParams]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('2fa-trigger', handle2FATrigger);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('2fa-trigger', handle2FATrigger);
    };
  }, [handlePopState, handle2FATrigger]);

  useEffect(() => {
    if (isAuthPage(pathname)) {
      const timer = setTimeout(() => {
        preloadModalChunk();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [pathname, preloadModalChunk]);

  if (shouldMount) {
    return <TwoFactorModalFlow />;
  }

  return null;
}

export const trigger2FAModal = (type: (typeof TWO_FA_MODAL_VALUES)[number]): void => {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set('modal', type);
  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
  window.history.pushState({}, '', newUrl);
  window.dispatchEvent(new CustomEvent('2fa-trigger', { detail: { show: true, type } }));
};
